// ─── Gestão de destinatários de notificações (Fase C, Move Express) ─────
// Lista/adiciona/edita/remove os responsáveis EXTERNOS que recebem email/SMS
// a cada marco. Renderizado como secção "Notificações" do PreferencesDialog.
import React, { useState, useEffect, useContext, useCallback } from 'react';
import { Box, Typography, TextField, Button, IconButton, Checkbox, FormControlLabel, CircularProgress } from '@material-ui/core';
import DeleteIcon from '@material-ui/icons/Delete';
import AddIcon from '@material-ui/icons/Add';
import EditIcon from '@material-ui/icons/Edit';
import { AuthContext } from '../../App';

const MILESTONES = [
  { key: 'arrived_terminal', label: 'Chegada ao terminal / carregamento' },
  { key: 'arrived_pa', label: 'Chegada ao posto de abastecimento' },
  { key: 'unloading', label: 'Descarga concluída' },
  { key: 'completed', label: 'Serviço terminado' },
];
const CHANNELS = [
  { key: 'email', label: 'Email' },
  { key: 'sms', label: 'SMS' },
];

const emptyForm = () => ({ id: null, name: '', email: '', phone: '', milestones: [], channels: ['email', 'sms'], isActive: true });

function NotificationRecipients({ lang = 'pt' }) {
  const T = (pt, en) => (lang === 'pt' ? pt : en);
  const { token } = useContext(AuthContext);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState(null); // null = sem editor aberto
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const r = await fetch('/planner-api/notification-recipients', { headers: { Authorization: `Bearer ${token}` } });
      const data = await r.json();
      setRows(Array.isArray(data) ? data : []);
    } catch { setRows([]); }
    setLoading(false);
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const toggleIn = (arr, key) => (arr.includes(key) ? arr.filter(x => x !== key) : [...arr, key]);

  async function save() {
    if (!form.name.trim()) { setError(T('Indica o nome.', 'Enter a name.')); return; }
    if (!form.email.trim() && !form.phone.trim()) { setError(T('Indica email ou telemóvel.', 'Enter email or phone.')); return; }
    setSaving(true); setError(null);
    try {
      const method = form.id ? 'PUT' : 'POST';
      const url = form.id ? `/planner-api/notification-recipients/${form.id}` : '/planner-api/notification-recipients';
      const r = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(form),
      });
      if (!r.ok) { const d = await r.json().catch(() => ({})); throw new Error(d.error || 'erro'); }
      setForm(null);
      await load();
    } catch (e) { setError(e.message || T('Não consegui guardar.', 'Could not save.')); }
    setSaving(false);
  }

  async function remove(id) {
    try {
      await fetch(`/planner-api/notification-recipients/${id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
      await load();
    } catch { /* silencioso */ }
  }

  const chip = (text, color) => (
    <Box component="span" style={{ display: 'inline-block', fontSize: '0.6rem', fontWeight: 700, padding: '1px 7px', borderRadius: 999, background: `${color}18`, color, marginRight: 4, marginTop: 2 }}>{text}</Box>
  );

  return (
    <Box>
      <Typography style={{ fontSize: '0.78rem', color: '#64748b', marginBottom: 12 }}>
        {T('Responsáveis do cliente que recebem email/SMS a cada marco do serviço. Os operadores são avisados dentro da plataforma automaticamente.',
          'Client managers who receive email/SMS at each service milestone. Operators are notified inside the platform automatically.')}
      </Typography>

      {loading ? (
        <Box style={{ display: 'flex', justifyContent: 'center', padding: 24 }}><CircularProgress size={24} /></Box>
      ) : (
        <>
          {rows.length === 0 && !form && (
            <Typography style={{ fontSize: '0.74rem', color: '#94a3b8', fontStyle: 'italic', marginBottom: 12 }}>
              {T('Ainda não há destinatários.', 'No recipients yet.')}
            </Typography>
          )}
          {rows.map(r => (
            <Box key={r.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: 10, marginBottom: 8, background: r.isActive ? '#fff' : '#f8fafc' }}>
              <Box style={{ flex: 1, minWidth: 0 }}>
                <Typography style={{ fontSize: '0.82rem', fontWeight: 700, color: '#0f172a' }}>
                  {r.name} {!r.isActive && <span style={{ fontSize: '0.62rem', color: '#94a3b8' }}>({T('inativo', 'inactive')})</span>}
                </Typography>
                <Typography style={{ fontSize: '0.7rem', color: '#475569' }}>
                  {[r.email, r.phone].filter(Boolean).join(' · ') || '—'}
                </Typography>
                <Box style={{ marginTop: 4 }}>
                  {(Array.isArray(r.channels) && r.channels.length ? r.channels : ['email', 'sms']).map(c => chip(c.toUpperCase(), '#2563eb'))}
                  {(Array.isArray(r.milestones) && r.milestones.length)
                    ? r.milestones.map(m => chip((MILESTONES.find(x => x.key === m) || {}).label || m, '#9a3412'))
                    : chip(T('Todos os marcos', 'All milestones'), '#16a34a')}
                </Box>
              </Box>
              <IconButton size="small" onClick={() => setForm({ id: r.id, name: r.name || '', email: r.email || '', phone: r.phone || '', milestones: Array.isArray(r.milestones) ? r.milestones : [], channels: Array.isArray(r.channels) && r.channels.length ? r.channels : ['email', 'sms'], isActive: r.isActive })}>
                <EditIcon style={{ fontSize: 16 }} />
              </IconButton>
              <IconButton size="small" onClick={() => remove(r.id)}>
                <DeleteIcon style={{ fontSize: 16, color: '#dc2626' }} />
              </IconButton>
            </Box>
          ))}

          {form ? (
            <Box style={{ marginTop: 12, padding: 14, border: '1.5px solid #fed7aa', borderRadius: 12, background: '#fff7ed' }}>
              <Typography style={{ fontSize: '0.74rem', fontWeight: 800, color: '#9a3412', marginBottom: 10 }}>
                {form.id ? T('Editar destinatário', 'Edit recipient') : T('Novo destinatário', 'New recipient')}
              </Typography>
              <TextField label={T('Nome', 'Name')} value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} fullWidth size="small" style={{ marginBottom: 10 }} />
              <Box style={{ display: 'flex', gap: 10, marginBottom: 10 }}>
                <TextField label="Email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} fullWidth size="small" />
                <TextField label={T('Telemóvel', 'Phone')} value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} fullWidth size="small" placeholder="+244 9XX XXX XXX" />
              </Box>
              <Typography style={{ fontSize: '0.64rem', fontWeight: 800, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 4 }}>{T('Canais', 'Channels')}</Typography>
              <Box style={{ display: 'flex', gap: 4, marginBottom: 8, flexWrap: 'wrap' }}>
                {CHANNELS.map(c => (
                  <FormControlLabel key={c.key} control={<Checkbox size="small" checked={form.channels.includes(c.key)} onChange={() => setForm(f => ({ ...f, channels: toggleIn(f.channels, c.key) }))} />} label={<span style={{ fontSize: '0.74rem' }}>{c.label}</span>} />
                ))}
              </Box>
              <Typography style={{ fontSize: '0.64rem', fontWeight: 800, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 4 }}>
                {T('Marcos (vazio = todos)', 'Milestones (empty = all)')}
              </Typography>
              <Box style={{ display: 'flex', flexDirection: 'column' }}>
                {MILESTONES.map(m => (
                  <FormControlLabel key={m.key} control={<Checkbox size="small" checked={form.milestones.includes(m.key)} onChange={() => setForm(f => ({ ...f, milestones: toggleIn(f.milestones, m.key) }))} />} label={<span style={{ fontSize: '0.74rem' }}>{m.label}</span>} />
                ))}
              </Box>
              <FormControlLabel control={<Checkbox size="small" checked={form.isActive} onChange={e => setForm(f => ({ ...f, isActive: e.target.checked }))} />} label={<span style={{ fontSize: '0.74rem' }}>{T('Ativo', 'Active')}</span>} />
              {error && <Typography style={{ fontSize: '0.7rem', color: '#dc2626', marginTop: 4 }}>⚠ {error}</Typography>}
              <Box style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                <Button variant="contained" disableElevation disabled={saving} onClick={save}
                  style={{ textTransform: 'none', fontWeight: 700, background: 'linear-gradient(135deg, #fb923c, #f97316)', color: '#fff' }}>
                  {saving ? T('A guardar…', 'Saving…') : T('Guardar', 'Save')}
                </Button>
                <Button onClick={() => { setForm(null); setError(null); }} style={{ textTransform: 'none', color: '#64748b' }}>{T('Cancelar', 'Cancel')}</Button>
              </Box>
            </Box>
          ) : (
            <Button startIcon={<AddIcon />} onClick={() => { setForm(emptyForm()); setError(null); }}
              style={{ textTransform: 'none', fontWeight: 700, color: '#9a3412', marginTop: 4 }}>
              {T('Adicionar destinatário', 'Add recipient')}
            </Button>
          )}
        </>
      )}
    </Box>
  );
}

export default NotificationRecipients;
